var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CognitoUserPool, CognitoUserAttribute, CognitoUser } from "amazon-cognito-identity-js";
import { CognitoIdentityProviderClient, InitiateAuthCommand, RespondToAuthChallengeCommand, AuthFlowType, ChallengeNameType, } from "@aws-sdk/client-cognito-identity-provider";
import axios from "axios";
import { Constants } from "../../../../utils/Constants";
const clientId = Constants.REACT_APP_CLIENT_ID || "";
const userPoolId = Constants.REACT_APP_USER_POOL_ID || "";
const googlePassword = Constants.REACT_APP_GOOGLE_PASSWORD || "";
const poolData = {
    UserPoolId: userPoolId,
    ClientId: clientId,
};
export const userPool = new CognitoUserPool(poolData);
// Cognito user pool region is the prefix of the pool id, e.g. "us-east-1_XXXXXXX" -> "us-east-1".
// Falls back to Constants.REACT_APP_AWS_REGION if that's defined and the pool id doesn't parse as expected.
const cognitoRegion = userPoolId.includes("_") ? userPoolId.split("_")[0] : Constants.REACT_APP_AWS_REGION || "us-east-1";
const cognitoClient = new CognitoIdentityProviderClient({ region: cognitoRegion });
// Re-exported so callers (e.g. OBLogin.tsx) don't need to import directly from the SDK package.
export { ChallengeNameType };
const storeTokens = (result) => {
    const accessToken = result.AccessToken || "";
    const idToken = result.IdToken || "";
    const refreshToken = result.RefreshToken || "";
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("idToken", idToken);
    localStorage.setItem("refreshToken", refreshToken);
    return { accessToken, idToken, refreshToken };
};
// user login check
// Uses AWS SDK v3 (InitiateAuthCommand) directly instead of amazon-cognito-identity-js's
// authenticateUser, because that library does not implement a callback for the EMAIL_OTP
// challenge (only SMS_MFA / SOFTWARE_TOKEN_MFA), which causes it to crash trying to read
// AuthenticationResult.NewDeviceMetadata off a challenge response that has no AuthenticationResult.
// See: https://github.com/aws-amplify/amplify-js/issues/14199
//
// NOTE: this replaces the old cognitoUser.authenticateUser(...) SRP-based flow. That flow's
// newPasswordRequired callback is not wired up anywhere in the admin UI today, so it has been
// dropped here; if the admin pool actually issues NEW_PASSWORD_REQUIRED challenges, that will
// come back from this function as a ChallengeName === "NEW_PASSWORD_REQUIRED" + Session, which
// the caller can special-case (it currently is NOT special-cased in OBLogin.tsx, and would fall
// into the MFA branch and confuse the user - flag this to the team before shipping if relevant).
export const checkUserLogin = (username, password) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    if (!username || !password) {
        throw new Error("Email and Password are required");
    }
    const normalizedUsername = username.toLocaleLowerCase();
    const command = new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: clientId,
        AuthParameters: {
            USERNAME: normalizedUsername,
            PASSWORD: password,
        },
    });
    const response = yield cognitoClient.send(command);
    // Fully authenticated, no MFA challenge issued
    if (response.AuthenticationResult) {
        return storeTokens(response.AuthenticationResult);
    }
    // Any MFA / additional challenge: EMAIL_OTP, SMS_MFA, SOFTWARE_TOKEN_MFA, SELECT_MFA_TYPE, etc.
    if (response.ChallengeName && response.Session) {
        return {
            mfaRequired: true,
            challengeName: response.ChallengeName,
            session: response.Session,
            username: normalizedUsername,
            destination: (_a = response.ChallengeParameters) === null || _a === void 0 ? void 0 : _a.CODE_DELIVERY_DESTINATION,
        };
    }
    throw new Error("Unexpected authentication response. Please try again.");
});
// Submit the OTP/MFA code for any challenge type returned by checkUserLogin,
// including EMAIL_OTP (which amazon-cognito-identity-js cannot handle).
export const submitMfaCode = (username, session, code, challengeName) => __awaiter(void 0, void 0, void 0, function* () {
    const challengeResponses = { USERNAME: username };
    if (challengeName === ChallengeNameType.EMAIL_OTP) {
        challengeResponses.EMAIL_OTP_CODE = code;
    }
    else if (challengeName === ChallengeNameType.SMS_MFA) {
        challengeResponses.SMS_MFA_CODE = code;
    }
    else if (challengeName === ChallengeNameType.SOFTWARE_TOKEN_MFA) {
        challengeResponses.SOFTWARE_TOKEN_MFA_CODE = code;
    }
    else {
        throw new Error(`Unsupported challenge type: ${challengeName}`);
    }
    const command = new RespondToAuthChallengeCommand({
        ClientId: clientId,
        ChallengeName: challengeName,
        Session: session,
        ChallengeResponses: challengeResponses,
    });
    const response = yield cognitoClient.send(command);
    if (!response.AuthenticationResult) {
        // Another challenge was returned instead of a final result (e.g. SELECT_MFA_TYPE chained
        // after a first response) - treat as a failure for now since the UI only supports a single
        // OTP step. Extend here if you need to support chained challenges.
        throw new Error("Verification failed. Please try again.");
    }
    return storeTokens(response.AuthenticationResult);
});
// Type guard: is a given value the MFA-challenge shape returned by checkUserLogin, as opposed to AuthTokens?
export const isMfaChallenge = (response) => {
    return !!response && response.mfaRequired === true;
};
// Function google sign up
// NOTE: signUpGoogle/checkUserLogin(googlePassword) below assume the Google-linked admin user
// never has MFA enabled on their pool (a fixed shared googlePassword is used for these accounts).
// If admin Google accounts can also be MFA-enrolled, this needs the same isMfaChallenge branch
// as the password sign-in flow in OBLogin.tsx - flagging this rather than guessing at desired UX.
export const signUpGoogle = (tokenResponse) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (tokenResponse && tokenResponse.access_token) {
                let userGoogleResponse = yield getGoogleUserData(tokenResponse).catch((error) => {
                    reject(error);
                    return;
                });
                if (userGoogleResponse) {
                    const userEmailID = userGoogleResponse.email;
                    // pre check if user already sign in
                    const userLoginResponsePreCheck = yield checkUserLogin(userEmailID, googlePassword).catch(() => { });
                    if (userLoginResponsePreCheck) {
                        resolve(userLoginResponsePreCheck);
                        return;
                    }
                    // If new user sign up using google cognito pool
                    const userPoolResponse = yield signUpCognito(userEmailID, googlePassword, "google").catch((error) => {
                        reject(error);
                        return;
                    });
                    if (userPoolResponse) {
                        // sign the user
                        const userLoginResponse = yield checkUserLogin(userEmailID, googlePassword).catch((error) => {
                            reject(error);
                            return;
                        });
                        resolve(userLoginResponse);
                    }
                }
            }
            else {
                reject(new Error("Invalid google login"));
            }
        }
        catch (error) {
            console.error("Error processing Google Sign-in response", error);
            reject(error);
        }
    }));
});
// send reset code
export const sentResetCode = (username) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        const userData = {
            Username: username,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);
        cognitoUser.forgotPassword({
            onSuccess: function (result) {
                resolve(result);
            },
            onFailure: function (err) {
                reject(err);
            },
        });
    }));
});
// change the password
export const resetPassword = (username, verificationCode, newPassword) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        const userData = {
            Username: username,
            Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);
        if (cognitoUser) {
            cognitoUser.confirmPassword(verificationCode, newPassword, {
                onSuccess: function (result) {
                    resolve(result);
                },
                onFailure: function (err) {
                    reject(err);
                },
            });
        }
        else {
            console.log("Invalid user emailId");
        }
    }));
});
// fetch user google login data
export const getGoogleUserData = (tokenResponse) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        const userInfo = yield axios
            .get("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        })
            .then((res) => resolve(res.data))
            .catch((error) => reject(error));
    }));
});
const generateUUIDv4 = () => {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));
};
// sign up google user pool
export const signUpCognito = (userEmail, password, domain) => __awaiter(void 0, void 0, void 0, function* () {
    return yield new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            let attributeList = [];
            if (domain === "google") {
                const dataDomain = {
                    Name: "custom:domain",
                    Value: "google",
                };
                const attributeDomain = new CognitoUserAttribute(dataDomain);
                attributeList.push(attributeDomain);
                password = googlePassword;
            }
            const dataTenantId = {
                Name: "custom:tenant_id",
                Value: generateUUIDv4(),
            };
            const attributeTenantId = new CognitoUserAttribute(dataTenantId);
            attributeList.push(attributeTenantId);
            userPool.signUp(userEmail.toLocaleLowerCase(), password, attributeList, [], function (error, result) {
                if (error && error instanceof Error) {
                    reject(error);
                    return;
                }
                resolve(result);
            });
        }
        catch (error) {
            console.error("Error user pool Sign-in response", error);
            reject(error);
        }
    }));
});
export const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("idToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("baseLocation");
    localStorage.removeItem("walletId");
    localStorage.removeItem("showBanner");
    localStorage.removeItem("rowsPerPage");
    Object.keys(localStorage).forEach(function (key) {
        if (key.startsWith("CognitoIdentityServiceProvider")) {
            localStorage.removeItem(key);
        }
    });
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
        cognitoUser.signOut();
        console.log("User signed out successfully");
    }
    else {
        console.log("No user to sign out");
    }
};
// Uses AWS SDK v3 (InitiateAuthCommand, REFRESH_TOKEN_AUTH) instead of
// amazon-cognito-identity-js's userPool.getCurrentUser()/cognitoUser.refreshSession().
// That library's getCurrentUser() only returns a user if authenticateUser() populated its
// own localStorage-backed session cache - since sign-in now goes through the raw SDK
// (checkUserLogin/submitMfaCode above) to support EMAIL_OTP, that cache is never populated,
// so getCurrentUser() would always be null here and every refresh attempt would fall through to logout().
export const refreshToken = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (!storedRefreshToken) {
        logout();
        window.location.pathname = "/login";
        return;
    }
    try {
        const command = new InitiateAuthCommand({
            AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
            ClientId: clientId,
            AuthParameters: {
                REFRESH_TOKEN: storedRefreshToken,
            },
        });
        const response = yield cognitoClient.send(command);
        if (!((_a = response.AuthenticationResult) === null || _a === void 0 ? void 0 : _a.AccessToken)) {
            throw new Error("Failed to refresh session");
        }
        const { AccessToken, IdToken, RefreshToken } = response.AuthenticationResult;
        localStorage.setItem("accessToken", AccessToken);
        if (IdToken) {
            localStorage.setItem("idToken", IdToken);
        }
        // Cognito generally does not rotate the refresh token unless refresh token rotation
        // is enabled on the app client - only overwrite it if a new one is actually returned.
        if (RefreshToken) {
            localStorage.setItem("refreshToken", RefreshToken);
        }
        return AccessToken;
    }
    catch (error) {
        console.error("Error refreshing session:", error);
        logout();
        window.location.pathname = "/login";
        throw error;
    }
});
